import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  IndianRupee,
  Clock,
  CheckCircle2,
  XCircle,
  Search,
  ArrowUpRight,
} from 'lucide-react';
import { DataTable, type Column, type TableDensity } from '@/components/common/DataTable';
import { DensityToggle } from '@/components/common/DensityToggle';
import { ListPageHeader } from '@/components/common/ListPageHeader';
import { ErrorState } from '@/components/common/ErrorState';
import { KpiCard } from '@/components/common/KpiCard';
import { StatusTabs, type StatusTabOption } from '@/components/common/StatusTabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { refundsService } from '@/services/refunds';
import type { Refund, RefundStatus } from '@/types/refunds';
import { fmt } from '@/lib/format';
import { useDebounced } from '@/hooks/useDebounced';
import { RefundDetailSheet } from './RefundDetailSheet';

type StatusFilter = 'all' | RefundStatus;

const STATUS_LABEL: Record<RefundStatus, string> = {
  REQUESTED: 'Requested',
  APPROVED: 'Approved',
  INITIATED: 'Initiated',
  PROCESSED: 'Processed',
  FAILED: 'Failed',
  REJECTED: 'Rejected',
  PARTIAL_REFUND: 'Partial',
};

const STATUS_TONE: Record<RefundStatus, 'muted' | 'success' | 'warning' | 'destructive'> = {
  REQUESTED: 'warning',
  APPROVED: 'warning',
  INITIATED: 'warning',
  PROCESSED: 'success',
  FAILED: 'destructive',
  REJECTED: 'destructive',
  PARTIAL_REFUND: 'success',
};

export function RefundsPage() {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<StatusFilter>('all');
  const [selected, setSelected] = useState<Refund | null>(null);
  const [density, setDensity] = useState<TableDensity>('compact');
  useDebounced(search); // search currently not wired to BE; kept for parity

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['refunds', { q: search }],
    queryFn: () => refundsService.list({ pageSize: 200 }),
  });

  const allRows = useMemo(() => {
    const rows = data?.rows ?? [];
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter(
      (r) =>
        r.refundNumber.toLowerCase().includes(q) ||
        r.salesOrder?.orderNumber.toLowerCase().includes(q) ||
        (r.externalRefundRef ?? '').toLowerCase().includes(q),
    );
  }, [data, search]);

  const counts = useMemo(() => {
    const c: Record<StatusFilter, number> = {
      all: allRows.length,
      REQUESTED: 0,
      APPROVED: 0,
      INITIATED: 0,
      PROCESSED: 0,
      FAILED: 0,
      REJECTED: 0,
      PARTIAL_REFUND: 0,
    };
    for (const r of allRows) c[r.status] += 1;
    return c;
  }, [allRows]);

  const filtered = useMemo(() => {
    if (filter === 'all') return allRows;
    return allRows.filter((r) => r.status === filter);
  }, [allRows, filter]);

  const summary = useMemo(() => {
    const open = counts.REQUESTED + counts.APPROVED + counts.INITIATED;
    const completed = counts.PROCESSED + counts.PARTIAL_REFUND;
    const failed = counts.FAILED + counts.REJECTED;
    const totalProcessed = allRows
      .filter((r) => r.status === 'PROCESSED' || r.status === 'PARTIAL_REFUND')
      .reduce((s, r) => s + Number(r.amount), 0);
    return { open, completed, failed, totalProcessed };
  }, [allRows, counts]);

  const tabs: StatusTabOption[] = [
    { value: 'all',       label: 'All',       count: counts.all },
    { value: 'REQUESTED', label: 'Requested', count: counts.REQUESTED },
    { value: 'APPROVED',  label: 'Approved',  count: counts.APPROVED },
    { value: 'INITIATED', label: 'Initiated', count: counts.INITIATED },
    { value: 'PROCESSED', label: 'Processed', count: counts.PROCESSED },
    { value: 'FAILED',    label: 'Failed',    count: counts.FAILED },
    { value: 'REJECTED',  label: 'Rejected',  count: counts.REJECTED },
  ];

  const columns: Column<Refund>[] = [
    {
      key: 'no',
      header: 'Refund',
      width: '170px',
      cell: (r) => (
        <div className="space-y-0.5">
          <div className="font-mono text-[12px] font-medium text-foreground">{r.refundNumber}</div>
          <div className="font-mono text-[10.5px] text-muted-foreground">
            {r.salesOrder?.orderNumber ?? '—'}
          </div>
        </div>
      ),
    },
    {
      key: 'type',
      header: 'Type',
      width: '110px',
      cell: (r) => (
        <Badge variant="muted" className="text-[10px]">
          {r.type}
        </Badge>
      ),
    },
    {
      key: 'amount',
      header: 'Amount',
      align: 'right',
      width: '130px',
      cell: (r) => (
        <span className="font-mono text-[12.5px] tabular-nums">{fmt.inr(Number(r.amount))}</span>
      ),
    },
    {
      key: 'provider',
      header: 'Provider',
      width: '110px',
      cell: (r) =>
        r.provider ? (
          <span className="font-mono text-[11.5px] text-muted-foreground">{r.provider}</span>
        ) : (
          <span className="text-[11.5px] text-muted-foreground">—</span>
        ),
    },
    {
      key: 'reference',
      header: 'Reference',
      width: '180px',
      cell: (r) =>
        r.externalRefundRef ? (
          <span className="font-mono text-[11.5px]">{r.externalRefundRef}</span>
        ) : r.providerRefundId ? (
          <span className="font-mono text-[11.5px] text-muted-foreground">{r.providerRefundId}</span>
        ) : (
          <span className="text-[11.5px] text-muted-foreground">—</span>
        ),
    },
    {
      key: 'created',
      header: 'Created at',
      width: '180px',
      cell: (r) => (
        <span className="font-mono text-[11.5px] tabular-nums text-muted-foreground">
          {fmt.dateTime(r.createdAt)}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      width: '120px',
      cell: (r) => (
        <Badge variant={STATUS_TONE[r.status]} className="text-[10px]">
          {STATUS_LABEL[r.status]}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      width: '60px',
      hoverReveal: true,
      cell: (r) => (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7 text-muted-foreground hover:text-foreground"
              onClick={() => setSelected(r)}
            >
              <ArrowUpRight className="h-3.5 w-3.5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Open</TooltipContent>
        </Tooltip>
      ),
    },
  ];

  return (
    <TooltipProvider delayDuration={150}>
      <div className="space-y-4">
        <ListPageHeader title="Refunds" count={counts.all} />

        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <KpiCard label="Open" value={fmt.number(summary.open)} tone="primary" icon={Clock} />
          <KpiCard label="Processed" value={fmt.number(summary.completed)} tone="success" icon={CheckCircle2} />
          <KpiCard
            label="Failed / rejected"
            value={fmt.number(summary.failed)}
            tone={summary.failed ? 'destructive' : 'default'}
            icon={XCircle}
          />
          <KpiCard
            label="Refunded value"
            value={fmt.inr(summary.totalProcessed)}
            tone="default"
            icon={IndianRupee}
          />
        </div>

        <StatusTabs value={filter} onChange={(v) => setFilter(v as StatusFilter)} options={tabs} />

        <div className="flex items-center justify-between gap-3">
          <div className="relative w-full max-w-sm">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search refund # / order # / ref…"
              className="h-8 pl-8 text-[13px]"
            />
          </div>
          <DensityToggle value={density} onChange={setDensity} />
        </div>

        {error ? (
          <ErrorState error={error} onRetry={() => refetch()} />
        ) : (
          <DataTable
            columns={columns}
            rows={filtered}
            rowKey={(r) => r.id}
            isLoading={isLoading}
            onRowClick={setSelected}
            density={density}
            emptyTitle={
              filter === 'all'
                ? 'No refunds yet'
                : `No ${STATUS_LABEL[filter as RefundStatus].toLowerCase()} refunds`
            }
            emptyDescription={
              filter === 'all'
                ? 'Refunds are issued from the order detail when money needs to flow back to the customer.'
                : 'Try a different status filter.'
            }
          />
        )}

        <RefundDetailSheet
          refund={selected}
          open={!!selected}
          onOpenChange={(o) => !o && setSelected(null)}
        />
      </div>
    </TooltipProvider>
  );
}
